import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as BlobSVG } from '../../../assets/blob.svg';
import Button from '../../Button/Button';
import './Navigation.css';
import { animateMenuOpen, animateMenuClose, initializeNavigationAnimations, animateNavHide, animateNavShow } from './NavigationAnimation';

const Navigation = ({ isFixed = false, scrollThreshold = '100svh' }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isNavVisible, setIsNavVisible] = useState(true);
    const menuRef = useRef(null);
    const hamburgerRef = useRef(null);
    const headerRef = useRef(null);
    const logoRef = useRef(null);
    const lastScrollTop = useRef(0);
    const scrollDownThreshold = '3svh';
    const scrollUpThreshold = '1svh';
    const location = useLocation();
    const navigate = useNavigate();
    const isHomePage = location.pathname === '/';

    const toggleMenu = () => {
        if (isMenuOpen) {
            animateMenuClose(menuRef.current, hamburgerRef.current).then(() => {
                setIsMenuOpen(false);
            });
        } else {
            animateMenuOpen(menuRef.current, hamburgerRef.current);
            setIsMenuOpen(true);
        }
    };

    const closeMenu = () => {
        if (isMenuOpen) {
            animateMenuClose(menuRef.current, hamburgerRef.current).then(() => {
                setIsMenuOpen(false);
            });
        }
    };

    const handleResize = () => {
        setIsMobile(window.innerWidth <= 768);
    };

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const windowHeight = window.innerHeight;
            const scrollThresholdPx = (parseFloat(scrollThreshold) / 100) * windowHeight;
            const newIsScrolled = currentScrollTop > scrollThresholdPx;

            if (newIsScrolled !== isScrolled) {
                setIsScrolled(newIsScrolled);
                setIsNavVisible(true);
                if (newIsScrolled) {
                    animateNavShow(headerRef.current);
                }
            }

            const scrollDifference = currentScrollTop - lastScrollTop.current;
            const scrollDownThresholdPx = (parseFloat(scrollDownThreshold) / 100) * windowHeight;
            const scrollUpThresholdPx = (parseFloat(scrollUpThreshold) / 100) * windowHeight;

            if (scrollDifference > scrollDownThresholdPx && isNavVisible) {
                setIsNavVisible(false);
                animateNavHide(headerRef.current);
            } else if (scrollDifference < -scrollUpThresholdPx && !isNavVisible) {
                setIsNavVisible(true);
                animateNavShow(headerRef.current);
            }

            lastScrollTop.current = currentScrollTop;
            if (isMenuOpen) closeMenu();
        };

        const handleClickOutside = (event) => {
            if (isMenuOpen && menuRef.current && !menuRef.current.contains(event.target) &&
                hamburgerRef.current && !hamburgerRef.current.contains(event.target)) {
                closeMenu();
            }
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleScroll);
        document.addEventListener('mousedown', handleClickOutside);

        initializeNavigationAnimations(menuRef.current);

        if (isFixed) {
            headerRef.current.classList.add('fixed');
        } else {
            headerRef.current.classList.remove('fixed');
        }

        const updateColors = () => {
            const logo = document.querySelector('.fns-logo');
            const navMenu = document.querySelector('.nav-menu');

            if (logo && navMenu) {
                if (isHomePage && !isScrolled) {
                    logo.style.color = 'var(--nav-color-light)';
                    navMenu.style.color = 'var(--nav-color-light)';
                } else if (isScrolled) {
                    logo.style.color = 'var(--nav-color-dark)';
                    navMenu.style.color = 'var(--nav-color-dark)';
                } else {
                    logo.style.color = 'var(--nav-color-dark)';
                    navMenu.style.color = 'var(--nav-color-dark)';
                }
            }
        };

        updateColors();

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleScroll);
            document.removeEventListener('mousedown', handleClickOutside);
            const logo = document.querySelector('.fns-logo');
            const navMenu = document.querySelector('.nav-menu');
            if (logo) logo.style.color = '';
            if (navMenu) navMenu.style.color = '';
        };
    }, [isMenuOpen, isNavVisible, isFixed, scrollThreshold, isScrolled, isHomePage]);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    const handleLogoClick = (e) => {
        e.preventDefault();
        scrollToTop();

        if (location.pathname !== '/') {
            navigate('/', { replace: true });
        }
    };

    const handleContactClick = (e) => {
        e.preventDefault();
        if (location.pathname === '/') {
          const formSection = document.getElementById('form-section');
          if (formSection) {
            formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        } else {
          navigate('/', { state: { scrollToForm: true } });
        }
        if (isMenuOpen) closeMenu();
      };

    const showHamburger = isMobile || isScrolled;

    const isActiveRoute = (path) => {
        return location.pathname === path;
    };

    const getButtonStyle = (path) => {
        return isActiveRoute(path) ? { color: '#BFBFBF' } : {};
    };

    return (
        <div className={`navigation-container ${isHomePage ? 'home-page' : ''}`}>
            <header ref={headerRef} className={`fixed-header ${isScrolled ? 'scrolled' : ''} ${isMobile ? 'mobile' : ''} ${isNavVisible ? 'visible' : 'hidden'}`}>
                <Link className="logo-link" to="/" onClick={handleLogoClick}>
                    <svg ref={logoRef} className="fns-logo" width="163" height="40" viewBox="0 0 163 21" xmlns="http://www.w3.org/2000/svg">
                        <g fill="currentColor">
                            <path d="M107.094 0.462947C105.201 1.0336 103.39 2.94935 103.061 4.62054C102.609 7.14771 103.802 8.77814 108.493 11.8352C113.184 14.8922 114.089 16.1151 113.101 18.1123C112.443 19.539 110.344 20.2319 108.863 19.5797C108.328 19.3352 107.505 18.3977 107.011 17.5009C105.859 15.2591 104.707 14.6477 103.596 15.789C102.773 16.5634 102.773 16.6857 103.39 17.9901C104.296 19.8651 106.518 20.8841 109.686 20.8841C115.447 20.8841 118.944 16.3189 116.27 12.3243C115.899 11.7537 113.801 10.1232 111.579 8.65586C106.806 5.55804 105.942 4.29446 107.299 2.46022C107.917 1.64501 108.534 1.31892 109.768 1.2374C111.496 1.11512 111.826 1.35968 114.13 4.62054C115.611 6.65858 118.121 5.06891 116.887 2.86783C115.529 0.462947 110.797 -0.719114 107.094 0.462947Z" />
                            <path d="M0.0281789 0.707515C0.0281789 0.952079 0.233916 1.2374 0.439653 1.35969C1.01572 1.68577 1.01572 19.3759 0.439653 19.702C-0.630179 20.3542 0.275063 20.721 2.9085 20.721C5.54193 20.721 6.44717 20.3542 5.37734 19.702C5.13046 19.539 4.96587 17.5009 4.96587 14.933V10.4086L6.69406 10.6531C7.76389 10.7754 8.71028 11.2238 9.1629 11.7129C9.53323 12.202 9.9447 12.5689 10.0681 12.5689C10.1916 12.5689 10.315 11.3461 10.315 9.83792V7.10695L9.1629 8.20749C8.33995 8.98194 7.55815 9.30803 6.48832 9.30803H4.96587V5.47652C4.96587 1.11512 5.08931 0.952079 8.17536 1.40045C9.45093 1.60425 10.5208 2.09338 11.6317 3.1124C12.4958 3.88685 13.3188 4.4575 13.4011 4.33522C13.5245 4.21294 13.4834 3.27544 13.3599 2.25642L13.0719 0.340668H6.57061C2.74391 0.340668 0.0281789 0.503711 0.0281789 0.707515Z" />
                            <path d="M16.1991 0.829797C14.8824 1.27817 14.4298 2.09338 15.2527 2.58251C15.4996 2.74555 15.6642 6.04717 15.6642 11.1423C15.6642 16.2374 15.4996 19.539 15.2527 19.702C14.1829 20.3542 15.0881 20.721 17.7216 20.721C20.355 20.721 21.2602 20.3542 20.1904 19.702C19.9435 19.539 19.7789 15.9928 19.7789 10.3678C19.7789 -0.270743 19.7789 -0.311504 16.1991 0.829797Z" />
                            <path d="M97.1771 0.992846C96.1485 1.44121 95.9016 1.72654 96.2719 2.09339C96.5188 2.33795 96.7245 3.60153 96.7245 4.86512V7.18848L95.2021 6.41402C92.7744 5.1912 89.5649 6.21022 88.0424 8.6151C85.4913 12.7727 86.3965 17.9086 90.0998 20.0689C91.7868 21.0471 94.5437 21.0879 95.9839 20.1504C96.9303 19.539 97.0537 19.539 97.2594 20.1096C97.4652 20.558 98.1235 20.721 99.8106 20.721C101.868 20.721 102.814 20.2319 101.58 19.8243C101.168 19.6613 101.004 17.2156 100.839 10.0825L100.634 0.544478L99.6048 0.462956C99.0288 0.422196 97.9589 0.66676 97.1771 0.992846ZM95.9839 8.37054C96.6011 8.94119 96.7245 9.67488 96.7245 13.4249C96.7245 18.2754 96.3953 19.0906 94.2557 19.0906C91.9514 19.0906 90.5113 15.626 91.1285 11.5906C91.6634 8.24826 94.0499 6.61783 95.9839 8.37054Z" />
                            <path d="M158.569 0.829797C157.252 1.27817 156.8 2.09338 157.623 2.58251C157.87 2.74555 158.034 6.04717 158.034 11.1423C158.034 16.2374 157.87 19.539 157.623 19.702C156.553 20.3542 157.458 20.721 160.092 20.721C162.725 20.721 163.63 20.3542 162.56 19.702C162.313 19.539 162.149 15.9928 162.149 10.3678C162.149 -0.270743 162.149 -0.311504 158.569 0.829797Z" />
                            <path d="M23.2765 1.40045C22.4947 2.21566 22.6181 3.96838 23.5233 4.53903C25.5807 5.80261 27.8438 2.99012 25.951 1.48197C24.8401 0.585235 24.1405 0.544474 23.2765 1.40045Z" />
                            <path d="M49.2404 2.90859C48.5821 4.4575 46.7716 6.04717 45.6606 6.04717C45.208 6.04717 44.8788 6.3325 44.8788 6.65858C44.8788 6.98467 45.2492 7.26999 45.6606 7.26999C46.4013 7.26999 46.4836 7.6776 46.607 12.7319C46.7305 18.7645 47.2242 20.0689 49.5285 20.8433C50.8041 21.2509 53.3552 20.2319 54.0958 19.0091C54.6308 18.1531 54.0958 17.5417 53.4375 18.1939C52.6145 19.0091 51.4624 18.7645 51.0509 17.6232C50.8041 17.0526 50.6395 14.4846 50.6395 11.9167V7.26999H52.2854C53.4786 7.26999 53.9312 7.10695 53.9312 6.65858C53.9312 6.21021 53.4786 6.04717 52.2854 6.04717H50.6395V3.80533C50.6395 1.27816 50.1045 0.911317 49.2404 2.90859Z" />
                            <path d="M122.483 2.90859C121.824 4.4575 120.014 6.04717 118.903 6.04717C118.45 6.04717 118.121 6.3325 118.121 6.65858C118.121 6.98467 118.492 7.26999 118.903 7.26999C119.644 7.26999 119.726 7.6776 119.849 12.7319C119.973 18.7645 120.467 20.0689 122.771 20.8433C124.046 21.2509 126.598 20.2319 127.338 19.0091C127.873 18.1531 127.338 17.5417 126.68 18.1939C125.857 19.0091 124.705 18.7645 124.293 17.6232C124.046 17.0526 123.882 14.4846 123.882 11.9167V7.26999H125.528C126.721 7.26999 127.174 7.10695 127.174 6.65858C127.174 6.21021 126.721 6.04717 125.528 6.04717H123.882V3.80533C123.882 1.27816 123.347 0.911317 122.483 2.90859Z" />
                            <path d="M35.7441 6.57706C33.8102 7.55532 33.769 7.55532 33.2341 6.7401C32.7403 6.00641 32.5758 5.96565 31.0533 6.45478C28.9959 7.10695 28.5433 7.55532 29.1605 8.16673C29.7777 8.77814 29.86 19.3352 29.2428 19.702C28.173 20.3542 29.0782 20.721 31.7117 20.721C34.3451 20.721 35.2503 20.3542 34.1805 19.702C33.481 19.2944 33.6867 10.6939 34.3862 9.30803C35.4561 7.26999 37.9661 7.14771 38.7067 9.10423C39.2416 10.5309 39.2416 19.3759 38.7067 19.702C37.6369 20.3542 38.5421 20.721 41.1756 20.721C43.809 20.721 44.7142 20.3542 43.6444 19.702C43.3975 19.539 43.2329 17.4602 43.2329 14.7292C43.2329 11.2238 43.0683 9.67488 42.5746 8.6151C41.2167 5.84337 38.6656 5.06891 35.7441 6.57706Z" />
                            <path d="M59.8976 6.00641C58.1694 6.45478 56.8527 7.31076 56.5235 8.28902C55.9886 9.87868 56.647 10.9385 58.1694 10.9385C58.6221 10.9385 59.1981 10.2455 59.8153 8.98195C61.2143 6.04718 63.3129 6.3325 63.9712 9.5526C64.2592 10.7754 64.2181 10.8162 61.667 11.7537C58.8278 12.7727 56.2355 14.9738 55.7829 16.7265C55.1657 19.0906 57.6345 21.4547 60.1034 20.9248C60.7617 20.7618 61.955 20.2319 62.7779 19.702C63.5597 19.1314 64.2181 18.8868 64.2181 19.0906C64.2181 19.9873 65.6171 20.721 67.3453 20.721C69.0323 20.721 69.6907 20.2727 68.7443 19.702C68.4974 19.539 68.3328 17.4194 68.3328 14.6885C68.3328 10.6939 68.2094 9.71564 67.5099 8.6151C66.1932 6.45478 62.7779 5.27272 59.8976 6.00641ZM64.2181 13.9548C64.2181 16.3596 63.642 17.827 62.4899 18.2346C60.4737 18.9683 59.3216 16.7673 60.556 14.5662C60.9675 13.7917 63.2306 12.1613 63.9301 12.1613C64.0947 12.1613 64.2181 12.9765 64.2181 13.9548Z" />
                            <path d="M77.303 6.57706C75.369 7.55532 75.3279 7.55532 74.793 6.7401C74.2992 6.00641 74.1346 5.96565 72.6122 6.45478C70.5548 7.10695 70.1022 7.55532 70.7194 8.16673C71.3366 8.77814 71.4189 19.3352 70.8017 19.702C69.7319 20.3542 70.6371 20.721 73.2705 20.721C75.904 20.721 76.8092 20.3542 75.7394 19.702C75.0399 19.2944 75.2456 10.6939 75.9451 9.30803C77.0149 7.26999 79.5249 7.14771 80.2656 9.10423C80.8005 10.5309 80.8005 19.3759 80.2656 19.702C79.1957 20.3542 80.101 20.721 82.7344 20.721C85.3679 20.721 86.2731 20.3542 85.2033 19.702C84.9564 19.539 84.7918 17.4602 84.7918 14.7292C84.7918 11.2238 84.6272 9.67488 84.1334 8.6151C82.7756 5.84337 80.2244 5.06891 77.303 6.57706Z" />
                            <path d="M133.305 6.29174C128.655 7.6776 126.639 13.5064 129.396 17.5417C131.412 20.558 134.992 21.6993 138.037 20.3542C140.053 19.4982 141.781 17.3787 141.369 16.3189C141.123 15.7075 141.04 15.7075 140.423 16.4819C138.242 19.2129 134.786 18.5607 132.934 15.0961C132.687 14.5662 132.564 14.0363 132.646 13.9548C132.77 13.8325 134.662 13.1395 136.884 12.4466C140.629 11.2645 140.958 11.0607 141.081 10.0417C141.287 8.41129 140.505 7.31076 138.571 6.41402C136.555 5.51728 135.856 5.51728 133.305 6.29174ZM136.267 7.26999C137.131 7.75912 137.296 9.30803 136.638 10.5309C136.226 11.3053 132.687 12.8542 132.276 12.4466C131.741 11.9575 132.687 8.73738 133.593 7.79988C134.662 6.7401 135.074 6.65858 136.267 7.26999Z" />
                            <path d="M147.706 6.29174C143.057 7.6776 141.04 13.5064 143.797 17.5417C145.813 20.558 149.393 21.6993 152.438 20.3542C154.454 19.4982 156.183 17.3787 155.771 16.3189C155.524 15.7075 155.442 15.7075 154.825 16.4819C152.644 19.2129 149.187 18.5607 147.336 15.0961C147.089 14.5662 146.965 14.0363 147.048 13.9548C147.171 13.8325 149.064 13.1395 151.286 12.4466C155.03 11.2645 155.36 11.0607 155.483 10.0417C155.689 8.41129 154.907 7.31076 152.973 6.41402C150.957 5.51728 150.257 5.51728 147.706 6.29174ZM150.669 7.26999C151.533 7.75912 151.697 9.30803 151.039 10.5309C150.628 11.3053 147.089 12.8542 146.677 12.4466C146.143 11.9575 147.089 8.73738 147.994 7.79988C149.064 6.7401 149.475 6.65858 150.669 7.26999Z" />
                            <path d="M23.5233 6.61782C22.0009 7.14771 21.5071 7.84064 22.2066 8.28901C22.7827 8.6151 22.8238 19.3352 22.2478 19.702C21.1779 20.3542 22.0832 20.721 24.7166 20.721C27.35 20.721 28.2553 20.3542 27.1855 19.702C26.9386 19.539 26.774 17.0526 26.774 13.5471C26.774 7.75912 26.4448 6.00641 25.4161 6.08793C25.1281 6.08793 24.3051 6.3325 23.5233 6.61782Z" />
                        </g>
                    </svg>
                </Link>
                {!showHamburger && (
                    <nav className="nav-menu">
                        <Button variant="nav-link" as={Link} to="/portfolio" style={getButtonStyle('/portfolio')}>Portfolio</Button>
                        <Button variant="nav-link" as={Link} to="/about" style={getButtonStyle('/about')}>Over ons</Button>
                        <Button variant="nav-link" onClick={handleContactClick}>Contact</Button>
                    </nav>
                )}
                {showHamburger && (
                    <div className="hamburger-wrapper">
                        <div ref={hamburgerRef} className="hamburger-container" onClick={toggleMenu}>
                            <span className="material-symbols-outlined">{isMenuOpen ? 'close' : 'drag_handle'}</span>
                        </div>
                        <nav ref={menuRef} className={`nav-menu hamburger-menu ${isMenuOpen ? 'active' : ''}`}>
                            <div className="menu-wrapper">
                                <div className="blob-left">
                                    <BlobSVG />
                                </div>
                                <div className="menu-section menu-section-text">
                                    <Button variant="staticlink" as={Link} to="/portfolio" onClick={closeMenu} style={getButtonStyle('/portfolio')}>Portfolio</Button>
                                    <Button variant="staticlink" as={Link} to="/about" onClick={closeMenu} style={getButtonStyle('/about')}>Over ons</Button>
                                    <Button variant="staticlink" onClick={handleContactClick}>Contact</Button>
                                </div>
                            </div>
                            <div className="blob-right">
                                <BlobSVG />
                            </div>
                        </nav>
                    </div>
                )}
            </header>
        </div>
    );
};

export default Navigation;
